// src/pages/TechnicalReportsPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { technicalReportService } from '../services/technicalReportService';
import { DocumentTextIcon, PlusCircleIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/solid';

const REPORT_TYPES = [
    { id: 'generico', name: 'Informe Genérico', description: 'Informe técnico de formato libre con secciones personalizables.', icon: '📋', color: 'bg-blue-600' },
    { id: 'imagenes', name: 'Informe de Imágenes', description: 'Registro fotográfico detallado con descripciones por imagen.', icon: '📸', color: 'bg-green-600' },
    { id: 'verificacion_inicial', name: 'Verificación Inicial (TE1)', description: 'Informe de verificación inicial de instalaciones eléctricas según normativa SEC.', icon: '⚡', color: 'bg-yellow-500' },
    { id: 'memoria_explicativa', name: 'Memoria Explicativa', description: 'Memoria técnica de proyecto eléctrico con cuadro de cargas y especificaciones.', icon: '📐', color: 'bg-purple-600' },
    { id: 'pericial', name: 'Informe Pericial', description: 'Informe pericial técnico para análisis de causa y recomendaciones.', icon: '🔍', color: 'bg-red-600' },
    { id: 'calidad_energia', name: 'Fallas de Calidad Energética', description: 'Análisis de calidad de suministro eléctrico, armónicos y factor de potencia.', icon: '📊', color: 'bg-orange-500' }
];

const TechnicalReportsPage = ({ setPage, setNotification }) => {
    const { user } = useAuth();
    const { isAdmin, canView, canCreate, canDelete } = usePermissions();
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showTypeSelector, setShowTypeSelector] = useState(false);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        loadReports();
    }, []);

    const loadReports = async () => {
        try {
            const data = await technicalReportService.getAll();
            setReports(data);
        } catch (err) {
            console.error(err);
            setNotification({ message: 'Error al cargar informes.', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!canDelete('techReports')) {
            setNotification({ message: 'No tienes permiso para eliminar informes.', type: 'error' });
            return;
        }
        if (!window.confirm('¿Eliminar este informe técnico permanentemente?')) return;
        try {
            await technicalReportService.delete(id);
            setReports(prev => prev.filter(r => r.id !== id));
            setNotification({ message: 'Informe eliminado.', type: 'success' });
        } catch (err) {
            setNotification({ message: 'Error al eliminar.', type: 'error' });
        }
    };

    const formatDate = (ts) => {
        if (!ts) return '-';
        if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleDateString('es-CL');
        return new Date(ts).toLocaleDateString('es-CL');
    };

    const getTypeName = (typeId) => REPORT_TYPES.find(t => t.id === typeId)?.name || typeId;
    const getTypeIcon = (typeId) => REPORT_TYPES.find(t => t.id === typeId)?.icon || '📄';
    const getTypeColor = (typeId) => REPORT_TYPES.find(t => t.id === typeId)?.color || 'bg-gray-500';

    const filteredReports = filter === 'all' ? reports : reports.filter(r => r.type === filter);

    return (
        <div className="p-4 md:p-8 animate-fade-in">
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                <div className="flex items-center gap-4 w-full md:w-auto">
                    {canCreate('techReports') && (
                        <button
                            onClick={() => setShowTypeSelector(true)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 shadow-lg shadow-blue-200 dark:shadow-none transition-all active:scale-95"
                        >
                            <PlusCircleIcon className="w-5 h-5" /> Nuevo Informe
                        </button>
                    )}
                    <select
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="block w-full md:w-auto px-4 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-xl shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm text-gray-900 dark:text-gray-100"
                    >
                        <option value="all">Todos los tipos</option>
                        {REPORT_TYPES.map(type => (
                            <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                    </select>
                </div>
                <div className="flex-grow">
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Informes Técnicos</h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">Crea y gestiona informes técnicos profesionales</p>
                </div>
                <button onClick={() => setPage('hub')} className="text-blue-600 dark:text-blue-400 hover:underline text-sm">← Volver</button>
            </div>

            {/* Filtro por Tipo */}
            <div className="flex flex-wrap gap-2 mb-6">
                <button
                    onClick={() => setFilter('all')}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                >
                    Todos ({reports.length})
                </button>
                {REPORT_TYPES.map(type => {
                    const count = reports.filter(r => r.type === type.id).length;
                    if (count === 0) return null;
                    return (
                        <button
                            key={type.id}
                            onClick={() => setFilter(type.id)}
                            className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${filter === type.id ? 'bg-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                        >
                            {type.icon} {type.name} ({count})
                        </button>
                    );
                })}
            </div>

            {/* Listado */}
            {loading ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">Cargando informes...</div>
            ) : filteredReports.length === 0 ? (
                <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
                    <DocumentTextIcon className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 text-lg">No hay informes técnicos aún.</p>
                    <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Presiona "Nuevo Informe" para crear el primero.</p>
                </div>
            ) : (
                <div className="grid gap-3">
                    {filteredReports.map(report => (
                        <div key={report.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 flex items-center justify-between hover:shadow-md transition group">
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                                <div className={`${getTypeColor(report.type)} text-white w-12 h-12 rounded-lg flex items-center justify-center text-xl shrink-0 shadow`}>
                                    {getTypeIcon(report.type)}
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-bold text-gray-800 dark:text-gray-100 truncate">{report.titulo || 'Sin título'}</h3>
                                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full font-medium">{getTypeName(report.type)}</span>
                                        {report.folio && <span className="font-mono text-[10px] text-gray-400 dark:text-gray-500">Folio: {report.folio}</span>}
                                        <span>{report.clientName || 'Sin cliente'}</span>
                                        <span>{formatDate(report.createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-4">
                                {canView('techReports') && (
                                    <button
                                        onClick={() => setPage('viewTechReport', report.id)}
                                        className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition"
                                        title="Ver"
                                    >
                                        <EyeIcon className="w-5 h-5" />
                                    </button>
                                )}
                                {canCreate('techReports') && ( // Assuming edit permission is tied to create for now
                                    <button
                                        onClick={() => setPage('editTechReport', report.id)}
                                        className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
                                        title="Editar"
                                    >
                                        ✏️
                                    </button>
                                )}
                                {canDelete('techReports') && (
                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(report.id); }} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition" title="Eliminar">
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal Selector de Tipo */}
            {showTypeSelector && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm" onClick={() => setShowTypeSelector(false)}>
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-2xl shadow-2xl max-h-[85vh] overflow-y-auto border border-transparent dark:border-gray-700" onClick={e => e.stopPropagation()}>
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-2">Seleccione Tipo de Informe</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Elija el formato de informe técnico que desea generar.</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {REPORT_TYPES.map(type => (
                                <button
                                    key={type.id}
                                    onClick={() => { setShowTypeSelector(false); setPage('techReportForm', type.id); }}
                                    disabled={!['generico', 'imagenes'].includes(type.id)}
                                    className={`text-left p-4 rounded-xl border-2 transition-all duration-200 group ${
                                        ['generico', 'imagenes'].includes(type.id)
                                            ? 'border-gray-200 dark:border-gray-600 hover:border-blue-500 hover:shadow-lg cursor-pointer'
                                            : 'border-gray-100 dark:border-gray-700 opacity-50 cursor-not-allowed'
                                    }`}
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="text-2xl">{type.icon}</span>
                                        <h3 className="font-bold text-gray-800 dark:text-gray-100 group-hover:text-blue-600 transition">{type.name}</h3>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{type.description}</p>
                                    {!['generico', 'imagenes'].includes(type.id) && (
                                        <span className="text-[10px] bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 px-2 py-0.5 rounded-full font-bold mt-2 inline-block">Próximamente</span>
                                    )}
                                </button>
                            ))}
                        </div>
                        <div className="mt-6 flex justify-end">
                            <button onClick={() => setShowTypeSelector(false)} className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition">Cancelar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TechnicalReportsPage;
